import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDiscoveryClient } from '../api/context';
import { useCreateEmailCampaign, useEmailCampaigns, useInstantlyCampaigns } from '../hooks/email';
import { useDiscoveryPaths } from '../workspace/paths';
import { CheckboxField, Note, Segmented } from '../workspace/ui';
import { describeFilter, splitCounts } from './model';
import { downloadBlob } from './parts';
import type { AudienceFilter, AudienceOrder, BatchResult } from './types';

export type SendMode = 'instantly' | 'csv';

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const ORDER_OPTIONS: { value: AudienceOrder; label: string }[] = [
  { value: 'followers', label: 'Biggest first' },
  { value: 'newest', label: 'Newest first' },
  { value: 'random', label: 'Random sample' },
];

const ORDER_HINTS: Record<AudienceOrder, string> = {
  followers: 'Takes the creators with the most followers.',
  newest: 'Takes the creators discovered most recently.',
  random: 'A fair sample of the segment — use this for a test batch, so its results predict the rest.',
};

const today = () => new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : 'Something went wrong');

/** One campaign's share of a split send. */
interface SplitPart {
  name: string;
  count: number;
  result: BatchResult | null;
  error: string | null;
}

/**
 * Hand the selected audience to Instantly, or download it. Either way the
 * batch is recorded, so these creators drop out of the next selection and
 * their results can be read later.
 */
export function SendDialog({
  mode,
  open,
  onOpenChange,
  filter,
  matching,
  maxBatch,
  linkBase,
  folderName,
}: {
  mode: SendMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: AudienceFilter;
  matching: number;
  maxBatch: number;
  /** Origin of tracked links, or null when the link is sent as written. */
  linkBase: string | null;
  folderName: (publicId: string) => string;
}): JSX.Element {
  const client = useDiscoveryClient();
  const paths = useDiscoveryPaths();
  const create = useCreateEmailCampaign();
  const instantlyCampaigns = useInstantlyCampaigns(open && mode === 'instantly');
  const linked = useEmailCampaigns(true).data?.campaigns ?? [];

  const [remoteIds, setRemoteIds] = useState<string[]>([]);
  // Several campaigns means a split: each gets its own batch, so each can hold a full one.
  const splitting = mode === 'instantly' && remoteIds.length > 1;
  const cap = Math.min(matching, maxBatch * Math.max(1, remoteIds.length));
  const [name, setName] = useState(mode === 'csv' ? `Export ${today()}` : '');
  const [count, setCount] = useState(String(cap));
  const [order, setOrder] = useState<AudienceOrder>('followers');
  const [linkTemplate, setLinkTemplate] = useState('');
  const [verify, setVerify] = useState(false);
  const [trackClicks, setTrackClicks] = useState(false);
  const [track, setTrack] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [parts, setParts] = useState<SplitPart[] | null>(null);

  // By name, numbers in order, so "Vyral 1 … Vyral 8" read as a set and stray campaigns stand apart.
  const options = useMemo(
    () => [...(instantlyCampaigns.data?.campaigns ?? [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [instantlyCampaigns.data],
  );
  const picked = remoteIds.length === 1 ? options.find((c) => c.id === remoteIds[0]) : undefined;
  const pickedLinked = linked.find((c) => c.publicId === picked?.linkedCampaignId);
  // A campaign that's been used before already has its link; don't make them retype it.
  useEffect(() => {
    if (pickedLinked?.linkTemplate) setLinkTemplate(pickedLinked.linkTemplate);
    if (pickedLinked) setTrackClicks(pickedLinked.trackClicks ?? false);
  }, [pickedLinked?.publicId, pickedLinked?.linkTemplate, pickedLinked?.trackClicks]);

  const n = Number(count);
  const countError = !Number.isInteger(n) || n < 1 ? 'Enter a whole number' : n > cap ? `At most ${cap.toLocaleString()}` : null;
  const partial = !countError && n < matching;
  const shares = splitCounts(countError ? 0 : n, remoteIds.length);
  // With already-emailed creators let back in, a later campaign could pick up someone an earlier one just took.
  const splitOverlaps = splitting && !!filter.includeEmailed;
  const summary = useMemo(() => describeFilter(filter, folderName), [filter, folderName]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const hasLink = linkTemplate.trim().length > 0;
      const batch = {
        filter,
        limit: n,
        order,
        ...(hasLink ? { linkTemplate: linkTemplate.trim(), trackClicks: trackClicks && !!linkBase } : {}),
      };
      if (splitting) {
        setParts(await sendSplit(batch));
      } else if (mode === 'instantly') {
        setResult(await create.mutateAsync({ ...batch, provider: 'instantly', providerCampaignId: remoteIds[0] ?? '', verify }));
      } else if (track) {
        const res = await create.mutateAsync({ ...batch, provider: 'csv', name: name.trim() });
        downloadBlob(await client.email.exportCampaignCsv(res.campaign.publicId), `${name.trim().replace(/[^\w.-]+/g, '-')}.csv`);
        setResult(res);
      } else {
        downloadBlob(await client.email.exportAudienceCsv(filter, { limit: n, order, linkTemplate: linkTemplate.trim() || undefined }), 'creator-emails.csv');
        onOpenChange(false);
      }
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * One campaign at a time, never in parallel: each batch leaves out everyone
   * already emailed, so running them in turn is what keeps the groups apart.
   * Random order makes each group a fair sample, so the campaigns can be compared.
   */
  async function sendSplit(batch: { filter: AudienceFilter; linkTemplate?: string; trackClicks?: boolean }): Promise<SplitPart[]> {
    const done: SplitPart[] = [];
    for (const [i, id] of remoteIds.entries()) {
      const name = options.find((c) => c.id === id)?.name ?? id;
      const share = shares[i] ?? 0;
      if (share === 0) continue;
      try {
        const res = await create.mutateAsync({ ...batch, limit: share, order: 'random', provider: 'instantly', providerCampaignId: id, verify });
        done.push({ name, count: share, result: res, error: null });
      } catch (e) {
        // Stop here: sending the rest would be fine, but the person should see this first.
        done.push({ name, count: share, result: null, error: messageOf(e) });
        break;
      }
    }
    return done;
  }

  const canSubmit =
    !busy && !countError && !splitOverlaps && (mode === 'instantly' ? remoteIds.length > 0 : !track || name.trim().length > 0);

  if (parts) return <SplitResult open={open} onOpenChange={onOpenChange} parts={parts} planned={shares.filter((s) => s > 0).length} />;

  if (result) {
    const where = mode === 'instantly' ? `"${result.campaign.name}" in Instantly` : `"${result.campaign.name}"`;
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {mode === 'instantly' ? `Added ${result.added.toLocaleString()} to ${where}` : `Exported ${result.added.toLocaleString()} addresses`}
            </DialogTitle>
            <DialogDescription>
              {mode === 'instantly'
                ? "They'll start receiving emails on that campaign's schedule. Nothing is sent from here."
                : 'The file has downloaded. These creators are now marked as emailed.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {result.skipped > 0 && (
              <Note tone="warning">
                Instantly didn't take {result.skipped.toLocaleString()} (on its block list, invalid, or already in the campaign).{' '}
                {result.skipped === 1 ? "It's" : "They're"} listed on the campaign page.
              </Note>
            )}
            {result.failed > 0 && (
              <Note tone="error" role="alert">
                {result.failed.toLocaleString()} couldn't be pushed{result.error ? `: ${result.error}` : ''}. Retry them from the campaign page.
              </Note>
            )}
            {result.recorded < result.selected && (
              <Note tone="info">
                {(result.selected - result.recorded).toLocaleString()} already in this campaign {result.selected - result.recorded === 1 ? "wasn't" : "weren't"} added again.
              </Note>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Done
            </Button>
            <Button asChild>
              <Link to={paths.emailCampaign(result.campaign.publicId)}>See results</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{mode === 'instantly' ? 'Send to Instantly' : 'Export CSV'}</DialogTitle>
          <DialogDescription>
            {matching.toLocaleString()} addresses selected: {summary}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {mode === 'instantly' ? (
            <div className="grid gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <Label>Instantly campaigns</Label>
                {options.length > 1 && (
                  <button
                    type="button"
                    className="text-sm text-brand hover:underline"
                    onClick={() => setRemoteIds(remoteIds.length === options.length ? [] : options.map((c) => c.id))}
                  >
                    {remoteIds.length === options.length ? 'Clear' : 'Pick all'}
                  </button>
                )}
              </div>
              <div className="panel grid max-h-56 gap-2 overflow-y-auto rounded-lg px-3 py-2.5">
                {instantlyCampaigns.isLoading ? (
                  <p className="text-sm text-muted-foreground">Loading campaigns…</p>
                ) : options.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No campaigns in Instantly yet</p>
                ) : (
                  options.map((c) => (
                    <CheckboxField
                      key={c.id}
                      checked={remoteIds.includes(c.id)}
                      onChange={(on) => setRemoteIds(on ? [...remoteIds, c.id] : remoteIds.filter((id) => id !== c.id))}
                      hint={c.linkedCampaignId ? `${c.statusLabel} · used before` : c.statusLabel}
                    >
                      {c.name}
                    </CheckboxField>
                  ))
                )}
              </div>
              {instantlyCampaigns.error ? (
                <Note tone="error" role="alert">
                  Couldn't load campaigns from Instantly: {(instantlyCampaigns.error as Error).message}
                </Note>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Write the emails and set the schedule in Instantly first, then pick the campaign here. Pick several to
                  split the creators evenly between them, a different random group each, to test one version against another.
                  {picked && picked.status !== 1 && ` This one is ${picked.statusLabel.toLowerCase()}, so nothing sends until you start it there.`}
                </p>
              )}
            </div>
          ) : (
            <div className="grid gap-3">
              <CheckboxField
                checked={track}
                onChange={setTrack}
                hint="Keeps these creators out of your next selection and counts who joins afterwards."
              >
                Record this export
              </CheckboxField>
              {track && (
                <div className="grid gap-1.5">
                  <Label htmlFor="export-name">Name</Label>
                  <Input id="export-name" value={name} onChange={(ev) => setName(ev.target.value)} maxLength={160} />
                </div>
              )}
            </div>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="batch-size">{splitting ? 'How many in total' : 'How many'}</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input id="batch-size" inputMode="numeric" className="w-28" value={count} onChange={(ev) => setCount(ev.target.value)} aria-invalid={!!countError} />
              <span className="text-sm text-muted-foreground">of {matching.toLocaleString()}</span>
              {partial && !splitting && <Segmented label="Which ones" value={order} onChange={setOrder} options={ORDER_OPTIONS} />}
            </div>
            {countError ? (
              <p className="text-sm text-destructive-strong">{countError}</p>
            ) : splitting ? (
              <p className="text-sm text-muted-foreground">
                {(shares.at(-1) ?? 0) === (shares[0] ?? 0)
                  ? `${(shares[0] ?? 0).toLocaleString()} each`
                  : `${(shares.at(-1) ?? 0).toLocaleString()}–${(shares[0] ?? 0).toLocaleString()} each`}{' '}
                across {remoteIds.length} campaigns, picked at random so each group is a fair sample.
              </p>
            ) : partial ? (
              <p className="text-sm text-muted-foreground">{ORDER_HINTS[order]}</p>
            ) : matching > maxBatch ? (
              <p className="text-sm text-muted-foreground">One batch holds up to {maxBatch.toLocaleString()}. Send the rest as another batch.</p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="link-template">Link for each creator (optional)</Label>
            <Input
              id="link-template"
              placeholder="https://apps.apple.com/app/id…?pt=…"
              value={linkTemplate}
              onChange={(ev) => setLinkTemplate(ev.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Use it in your emails as <code className="font-mono text-xs">{'{{invite_link}}'}</code>. For installs, paste an App Store campaign link (one with a <code className="font-mono text-xs">pt</code> provider token): a campaign
              token is added for this campaign and email type, and App Store Connect reports installs for it.
            </p>
          </div>

          {linkBase && linkTemplate.trim() && (mode === 'instantly' || track) && (
            <CheckboxField
              checked={trackClicks}
              onChange={setTrackClicks}
              hint={`Each creator gets their own link on ${hostOf(linkBase)} that forwards to yours. Leave off to send your link exactly as written.`}
            >
              Count clicks per creator
            </CheckboxField>
          )}

          {mode === 'instantly' && (
            <>
              <CheckboxField checked={verify} onChange={setVerify} hint="Removes addresses likely to bounce. Uses your Instantly verification credits.">
                Verify addresses in Instantly on import
              </CheckboxField>
              <p className="text-sm text-muted-foreground">
                Your emails can use <code className="font-mono text-xs">{'{{greeting_name}}'}</code> (first name, or the handle when
                there isn't a real one), <code className="font-mono text-xs">{'{{handle}}'}</code>,{' '}
                <code className="font-mono text-xs">{'{{platform}}'}</code>, <code className="font-mono text-xs">{'{{followers}}'}</code> and{' '}
                <code className="font-mono text-xs">{'{{profile_url}}'}</code>.
              </p>
            </>
          )}

          {splitOverlaps && (
            <Note tone="warning">
              "Include creators already in an email campaign" is on, so a creator could land in two of these campaigns.
              Switch it off on the Audience page to split.
            </Note>
          )}

          {error && (
            <Note tone="error" role="alert">
              {error}
            </Note>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'instantly' ? <Send className="h-4 w-4" /> : <Download className="h-4 w-4" />}
            {mode === 'instantly'
              ? splitting
                ? `Split ${countError ? '' : n.toLocaleString()} across ${remoteIds.length} campaigns`
                : `Add ${countError ? '' : n.toLocaleString()} to campaign`
              : `Export ${countError ? '' : n.toLocaleString()}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** What a split send did, campaign by campaign. */
function SplitResult({
  open,
  onOpenChange,
  parts,
  planned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parts: SplitPart[];
  /** How many campaigns were picked, to say how many never got their batch. */
  planned: number;
}): JSX.Element {
  const paths = useDiscoveryPaths();
  const added = parts.reduce((sum, p) => sum + (p.result?.added ?? 0), 0);
  const ok = parts.filter((p) => p.result).length;
  const stopped = parts.find((p) => p.error);
  const notReached = planned - parts.length;
  const skipped = parts.reduce((sum, p) => sum + (p.result?.skipped ?? 0), 0);
  const failed = parts.reduce((sum, p) => sum + (p.result?.failed ?? 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Added {added.toLocaleString()} across {ok} {ok === 1 ? 'campaign' : 'campaigns'}
          </DialogTitle>
          <DialogDescription>
            They'll start receiving emails on each campaign's schedule. Nothing is sent from here.
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-border text-sm">
          {parts.map((p) => (
            <li key={p.name} className="flex items-baseline justify-between gap-3 py-2">
              {p.result ? (
                <Link to={paths.emailCampaign(p.result.campaign.publicId)} className="min-w-0 truncate hover:underline">
                  {p.name}
                </Link>
              ) : (
                <span className="min-w-0 truncate">{p.name}</span>
              )}
              <span className={p.error ? 'shrink-0 text-destructive-strong' : 'shrink-0 font-mono tabular-nums text-muted-foreground'}>
                {p.result ? p.result.added.toLocaleString() : 'Not sent'}
              </span>
            </li>
          ))}
        </ul>
        <div className="space-y-2">
          {stopped && (
            <Note tone="error" role="alert">
              Stopped at "{stopped.name}": {stopped.error?.replace(/[.\s]*$/, '.')}
              {notReached > 0 && ` ${notReached} more ${notReached === 1 ? 'campaign was' : 'campaigns were'} not sent to.`} Send again with
              just those campaigns picked: nobody already added will be picked twice.
            </Note>
          )}
          {skipped > 0 && (
            <Note tone="warning">
              Instantly didn't take {skipped.toLocaleString()} (on its block list, invalid, or already in the campaign). They're listed on
              each campaign's page.
            </Note>
          )}
          {failed > 0 && (
            <Note tone="error" role="alert">
              {failed.toLocaleString()} couldn't be pushed. Retry them from each campaign's page.
            </Note>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
          <Button asChild>
            <Link to={paths.email('campaigns')}>See campaigns</Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
